import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './popup.module.scss';
import { Button, Select, Textarea, Typography } from '@/ui-kit';
import { FieldBlock } from '@/components/field-block';
import { Region, SearchParamsRegions } from '@/api/region/types';
import { renderCity } from '@/utils/render-city';
import { useGetRegions } from '@/hooks/use-get-regions';
import debounce from 'lodash/debounce';
import { FormProvider, useForm } from 'react-hook-form';
import { SelectOption } from '@/ui-kit/select';
import { isArray } from 'lodash';
import { ANY_REGION_OPTION } from '@/utils/regions';

interface Props {
    regions: null | Region[];
    onSubmit: (regions: SelectOption[]) => void;
    onClose: () => void;
}

const DELAY_SEND_DATA = 1000;

export const LocationProfile: React.FC<Props> = ({ regions, onSubmit, onClose }) => {
    const [searchParamsCities, setSearchParamsCities] = useState<SearchParamsRegions>({ q: '' });
    const [search, setSearch] = useState<string>('');

    const [isDisabled, setDisabled] = useState<boolean>(true);

    const { isLoading: isLoadingCities, data: dataRegions } = useGetRegions(searchParamsCities);

    const defaultValues = useMemo(() => {
        return {
            regions: regions
                ? regions.length
                    ? regions.map((region) => ({
                          label: renderCity(region),
                          value: region.id,
                      }))
                    : [ANY_REGION_OPTION]
                : [ANY_REGION_OPTION],
        };
    }, [regions]);

    const methods = useForm<{ regions: Array<{ label: string; value: string }> }>({
        // @ts-ignore
        defaultValues,
    });

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();

            // @ts-ignore
            onSubmit(methods.getValues('regions'));
        },
        [onSubmit],
    );


    const handleSearchCityDebounce = useCallback(
        (e: string): void => {
            setSearch(e);
            debounce((q: string) => {
                setSearchParamsCities({ q: q.trim() });
            }, DELAY_SEND_DATA)(e);
        },
        [searchParamsCities],
    );

    return (
        <div className={styles.editFormLocation}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit}>
                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography
                                variant="label"
                                fontWeight="medium"
                                className={styles.required}
                            >
                                Направление поиска
                            </Typography>
                        </div>
                        <Select
                            name="regions"
                            placeholder="Выберите город"
                            isLoading={isLoadingCities}
                            options={[
                                ...(search.length > 0 ? [] : [ANY_REGION_OPTION]),
                                ...(search.length > 0
                                    ? (dataRegions?.data?.map((region: Region) => ({
                                          label: renderCity(region),
                                          value: region.id,
                                      })) ?? [])
                                    : []),
                            ]}
                            isMulti={true}
                            isClearable={false}
                            onSearch={handleSearchCityDebounce}
                            onSelected={(val, setState) => {
                                if (isArray(val)) {
                                    if (val.length) {
                                        if (val[val.length - 1].value === -1) setState([ANY_REGION_OPTION]);
                                        else if (
                                            val.map((e: SelectOption) => e.value).includes(ANY_REGION_OPTION.value)
                                        )
                                            setState(
                                                val.filter((e: SelectOption) => e.value !== ANY_REGION_OPTION.value),
                                            );
                                    }
                                    const newRegions = methods.getValues('regions')
                                    const regionsIds = regions
                                        ?.map((e: Region) => e.id)
                                        .sort()
                                    const newRegionsIds = newRegions
                                        .map((e: Record<string, any>) => e.value)
                                        .sort()
                                    const isInitialRegionsWhenAnyRegionWasNotSelected =
                                        newRegionsIds.every(
                                            (e) => regionsIds?.includes(e)
                                        ) &&
                                        newRegionsIds.length === regionsIds?.length
                                    const isInitialRegionsWhenAnyRegionWasSelected =
                                        regionsIds?.length === 0 &&
                                        newRegionsIds
                                            .includes(
                                                ANY_REGION_OPTION.value
                                            )
                                    const isInitialRegions = isInitialRegionsWhenAnyRegionWasNotSelected ||
                                        isInitialRegionsWhenAnyRegionWasSelected
                                    setDisabled(
                                        newRegions.length === 0 ||
                                        isInitialRegions
                                    );
                                }
                            }}
                        />
                    </FieldBlock>
                    <Button
                        isDisabled={isDisabled}
                        type="submit"
                        variant="stretched"
                        typeColor="orange"
                    >
                        Применить
                    </Button>
                </form>

                <Button
                    variant="stretched"
                    typeColor="white"
                    onClick={onClose}
                >
                    Отмена
                </Button>
            </FormProvider>
        </div>
    );
};
